import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import { fetchAccess, formatPrice, getViewerEmail, verifyPackageCheckout } from "./learnApi";
import PackageSheet from "./PackageSheet";
import "./Learn.sass";

/**
 * Access state shared by every hub screen:
 *   mode          'coming_soon' | 'live'
 *   email         viewer email (or "")
 *   isMember      active Postaway membership
 *   isAttendee    on the 2026 summit list → discounted attendee rate
 *   entitlements  [{ packageSlug, source }]  (Summit recordings etc.)
 *   packages      purchasable packages
 *   refresh()     re-fetch after a purchase / claim
 */
export const LearnAccessContext = createContext({
  mode: "coming_soon",
  email: "",
  isMember: false,
  isAttendee: false,
  entitlements: [],
  packages: [],
  loading: true,
  refresh: () => {},
});

export const useLearnAccess = () => useContext(LearnAccessContext);

// Same membership rule as /hosts (with Stripe fallbacks), so members whose
// subscription only exists in Stripe are not locked out.
const checkSubscription = async (email) => {
  if (!email) return false;
  try {
    const res = await fetch("/api/access/subscription", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    return Boolean(res.ok && data.ok && data.active);
  } catch (_) {
    return false;
  }
};

const LearnGate = ({ access }) => {
  const location = useLocation();
  const [sheet, setSheet] = useState(null); // null | 'buy' | 'claim'
  const next = `${location.pathname}${location.search || ""}`;
  const loginHref = `/login?next=${encodeURIComponent(next)}`;
  const summitPackages = (access.packages || []).filter((p) => p.slug.startsWith("summit-2026"));
  return (
    <div className="learn-container">
      <div className="learn-gate">
        <div className="learn-eyebrow learn-eyebrow--light">Postaway Academy · Members</div>
        <h1 className="learn-gate__title">Made for Postaway members.</h1>
        <p className="learn-gate__text">
          Courses, guides and the Hospitality Summit recordings live here. Join Postaway to unlock the library,
          or pick up a Summit package on its own.
        </p>
        <div className="learn-gate__actions">
          <Link to="/creator-signup" className="learn-btn learn-btn--primary">Join as a creator</Link>
          <Link to="/host-select" className="learn-btn learn-btn--primary">Join as a host</Link>
          {!access.email && <Link to={loginHref} className="learn-btn learn-btn--ghost">Sign in</Link>}
        </div>
        {summitPackages.length > 0 && (
          <div className="learn-gate__packages">
            <div className="learn-eyebrow learn-eyebrow--light">Hospitality Summit 2026 recordings</div>
            <ul>
              {summitPackages.map((p) => (
                <li key={p.slug}>
                  <span>{p.title}</span>
                  <strong>
                    {access.isAttendee && p.attendee_price_cents != null ? (
                      <><s className="learn-price-was">{formatPrice(p.price_cents)}</s> {formatPrice(p.attendee_price_cents)}</>
                    ) : formatPrice(p.price_cents)}
                  </strong>
                </li>
              ))}
            </ul>
            <div className="learn-gate__actions">
              <button type="button" className="learn-btn learn-btn--primary" onClick={() => setSheet("buy")}>Buy a Summit package</button>
              <button type="button" className="learn-btn learn-btn--ghost" onClick={() => setSheet("claim")}>I'm a speaker — claim my access</button>
            </div>
            <p className="learn-gate__note">
              {access.isAttendee
                ? "You were at the 2026 Summit, so you get the attendee rate — it's applied automatically at checkout."
                : "Attended the 2026 Summit? Sign in or check out with the email you registered with and the attendee rate ($100 per track, $175 for both) is applied automatically. Speakers stream free."}
            </p>
          </div>
        )}
      </div>
      <PackageSheet
        open={sheet !== null}
        initialTab={sheet === "claim" ? "claim" : "buy"}
        onClose={() => setSheet(null)}
        packages={summitPackages}
        isAttendee={access.isAttendee}
        returnTo={location.pathname}
      />
    </div>
  );
};

/**
 * Wraps every Learning Hub screen: access resolution + the navy "theater"
 * theme. When `dark` is set the body gets `learn-dark` so the global Header
 * recolors itself to match.
 *
 *   coming_soon  → everyone sees the page (nothing plays). Sub-routes bounce
 *                  to /learn when `redirectWhenComingSoon` is set.
 *   live         → members and anyone with a package entitlement get in;
 *                  everyone else (logged out, or logged in without a
 *                  membership) sees LearnGate: sign in / join / buy / claim.
 */
const LearnShell = ({ dark = false, children, className, redirectWhenComingSoon = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({ loading: true, access: null, member: false });
  const [purchase, setPurchase] = useState(null); // { paid, packageTitle, email } after Stripe returns

  const load = useCallback(async () => {
    const email = getViewerEmail();
    const [accessRes, member] = await Promise.all([
      fetchAccess().catch(() => null),
      checkSubscription(email),
    ]);
    const access = accessRes || { mode: "coming_soon", email, isMember: false, isAttendee: false, entitlements: [], packages: [] };
    setState({ loading: false, access, member: member || Boolean(access.isMember) });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stripe embedded checkout returns to /learn[/slug]?session_id=cs_...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId || !sessionId.startsWith("cs_")) return undefined;
    let cancelled = false;
    verifyPackageCheckout(sessionId)
      .then((data) => {
        if (cancelled) return;
        setPurchase({ paid: Boolean(data.paid), packageTitle: data.packageTitle, email: data.email });
        if (data.paid) load();
      })
      .catch(() => { if (!cancelled) setPurchase({ paid: false }); })
      .finally(() => {
        if (typeof window !== "undefined") {
          window.history.replaceState({}, "", location.pathname);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dark) return undefined;
    document.body.classList.add("learn-dark");
    return () => document.body.classList.remove("learn-dark");
  }, [dark]);

  const mode = state.access?.mode || "coming_soon";
  const email = state.access?.email || getViewerEmail() || "";
  const entitlements = useMemo(() => state.access?.entitlements || [], [state.access]);
  const authorized = state.member || entitlements.length > 0;

  // Coming soon: nothing plays, sub-routes go home.
  useEffect(() => {
    if (state.loading) return;
    if (mode === "coming_soon" && redirectWhenComingSoon) navigate("/learn", { replace: true });
  }, [state.loading, mode, redirectWhenComingSoon, navigate]);

  const ctx = useMemo(() => ({
    mode,
    email,
    isMember: state.member,
    isAttendee: Boolean(state.access?.isAttendee),
    entitlements,
    packages: state.access?.packages || [],
    loading: state.loading,
    refresh: load,
  }), [mode, email, state.member, entitlements, state.access, state.loading, load]);

  let body;
  if (state.loading) {
    body = (
      <div className="learn-loading" aria-busy="true">
        <span className="learn-loading__dot" />
        <span className="learn-loading__dot" />
        <span className="learn-loading__dot" />
      </div>
    );
  } else if (mode === "coming_soon") {
    body = redirectWhenComingSoon ? null : children;
  } else if (authorized) {
    body = children;
  } else {
    // Live + (logged out | logged in without membership or a package):
    // the gate offers sign in, membership, or buying / claiming a package.
    body = <LearnGate access={ctx} />;
  }

  return (
    <LearnAccessContext.Provider value={ctx}>
      <div className={cn("learn", { "learn--dark": dark, "learn--light": !dark }, className)}>
        {purchase && (
          <div className={cn("learn-banner", { "learn-banner--ok": purchase.paid })} role="status">
            <div className="learn-container learn-banner__inner">
              <span>
                {purchase.paid ? (
                  <>
                    <strong>You're in!</strong> {purchase.packageTitle || "Your package"} is unlocked for{" "}
                    {purchase.email || "your email"}.{" "}
                    {email
                      ? "Sessions are ready below."
                      : "Check your inbox for a link to set your Postaway password, then sign in with that email."}
                  </>
                ) : (
                  <>Your payment wasn't completed, so nothing was charged. You can try again any time.</>
                )}
              </span>
              <button type="button" className="learn-banner__close" aria-label="Dismiss" onClick={() => setPurchase(null)}>×</button>
            </div>
          </div>
        )}
        {body}
      </div>
    </LearnAccessContext.Provider>
  );
};

export default LearnShell;
